export const ROBINHOOD_CHAIN = {
  chainId: "0x1237",
  chainName: "Robinhood Chain",
  nativeCurrency: { name: "Ether", symbol: "ETH", decimals: 18 },
  rpcUrls: ["https://rpc.mainnet.chain.robinhood.com"],
  blockExplorerUrls: ["https://robinhoodchain.blockscout.com"],
}

export function ethToWeiHex(amount: number) {
  const wei = BigInt(Math.round(amount * 1e18))
  return `0x${wei.toString(16)}`
}

export function shortenAddress(address?: string) {
  if (!address) return ""
  if (address.length < 12) return address
  return `${address.slice(0, 6)}...${address.slice(-4)}`
}

type EthereumProvider = {
  request: (args: { method: string; params?: unknown[] }) => Promise<unknown>
}

export function getEthereum(): EthereumProvider | null {
  const provider = (window as Window & { ethereum?: EthereumProvider }).ethereum
  return provider || null
}

export async function connectWallet() {
  const ethereum = getEthereum()
  if (!ethereum) {
    throw new Error("Install a wallet such as MetaMask or Robinhood Wallet.")
  }
  const accounts = (await ethereum.request({ method: "eth_requestAccounts" })) as string[]
  const address = accounts?.[0]
  if (!address) throw new Error("No wallet account selected.")
  try {
    await ethereum.request({
      method: "wallet_switchEthereumChain",
      params: [{ chainId: ROBINHOOD_CHAIN.chainId }],
    })
  } catch (error) {
    const code = (error as { code?: number })?.code
    if (code === 4902) {
      await ethereum.request({
        method: "wallet_addEthereumChain",
        params: [ROBINHOOD_CHAIN],
      })
    } else if (code !== 4001) {
      console.error("Chain switch failed:", error)
    }
  }
  return address
}

function utf8ToHex(text: string) {
  const bytes = new TextEncoder().encode(text)
  return `0x${Array.from(bytes).map((byte) => byte.toString(16).padStart(2, "0")).join("")}`
}

export async function mentionOnChain(payload: string) {
  const ethereum = getEthereum()
  if (!ethereum) {
    throw new Error("Install a wallet such as MetaMask or Robinhood Wallet.")
  }
  const from = await connectWallet()
  const data = utf8ToHex(payload.slice(0, 180))
  const txHash = (await ethereum.request({
    method: "eth_sendTransaction",
    params: [
      {
        from,
        to: from,
        value: "0x0",
        data,
      },
    ],
  })) as string
  return { from, txHash }
}

export async function mintImageOnChain(caption: string) {
  return mentionOnChain(`KLIPO mint image ${caption.trim() || "untitled"}`)
}

export async function burnImageOnChain(postId: string) {
  return mentionOnChain(`KLIPO burn image ${postId}`)
}

export async function transferImageOnChain(postId: string, toWallet: string) {
  return mentionOnChain(`KLIPO transfer ${postId} ${toWallet}`)
}

export async function sendEth(to: string, amountEth: number) {
  const ethereum = getEthereum()
  if (!ethereum) {
    throw new Error("Install a wallet such as MetaMask or Robinhood Wallet.")
  }
  const from = await connectWallet()
  const txHash = (await ethereum.request({
    method: "eth_sendTransaction",
    params: [
      {
        from,
        to,
        value: ethToWeiHex(amountEth),
      },
    ],
  })) as string
  return { from, txHash }
}
